import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCategory } from '../store/slice/categorySlice';
import { compressImageForUpload } from '../utils/imageCompress';

/** Must match Multer: upload.single('image') (or change both sides). */
const CATEGORY_IMAGE_FIELD = 'image';

function AddCategoryPage() {
  const navigate = useNavigate();
  const { createCategory, loading, error } = useCategory();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'Active',
  });
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) return;

    const isActive = formData.status === 'Active' ? 1 : 0;
    const baseFields = {
      name: formData.name.trim(),
      description: formData.description || '',
      is_active: isActive,
      display_order: 0,
    };

    let payload;
    if (imageFile) {
      const fileToSend = await compressImageForUpload(imageFile);
      const fd = new FormData();
      fd.append('name', baseFields.name);
      fd.append('description', baseFields.description);
      fd.append('is_active', String(baseFields.is_active));
      fd.append('display_order', String(baseFields.display_order));
      fd.append(CATEGORY_IMAGE_FIELD, fileToSend);
      payload = fd;
    } else {
      payload = baseFields;
    }

    const result = await createCategory(payload);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success('Category created successfully');
      navigate('/categories');
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <Link
          to="/categories"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Categories
        </Link>
      </div>

      <div className="card-premium overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h1 className="font-display font-bold text-2xl text-slate-800 tracking-tight">Add New Category</h1>
          <p className="mt-1 text-slate-500">Create a new service category (e.g., Hair, Nails, Skincare)</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
              Category Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Hair, Nails, Skincare"
              className="input-premium"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-1.5">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of this category"
              rows="4"
              className="input-premium resize-none"
            />
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-slate-700 mb-1.5">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="input-premium"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label htmlFor="category-image" className="block text-sm font-medium text-slate-700 mb-1.5">
              Category photo
            </label>
            <input
              id="category-image"
              name="category-image"
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              className="input-premium"
            />
            <p className="mt-1 text-xs text-slate-500">
              Optional. Large photos are resized before upload. If you still see “request too large”, raise the upload
              limit on your API server (for example nginx{' '}
              <span className="font-mono text-slate-600">client_max_body_size</span>).
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Category'}
            </button>
            <Link
              to="/categories"
              className="px-6 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-premium hover:bg-slate-50 transition-all"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCategoryPage;
